function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import * as React from "react";
import { useId } from "@react-aria/utils";
import AlertIcon from "@bolteu/kalep-react-icons/dist/Alert";
import CheckCircleFill from "@bolteu/kalep-react-icons/dist/CheckCircle";
import Cross from "@bolteu/kalep-react-icons/dist/Cross";
import InfoCircle from "@bolteu/kalep-react-icons/dist/InfoCircle";
import Offer from "@bolteu/kalep-react-icons/dist/Offer";
import { GhostButton } from "../GhostButton";
import { cx } from "../../helpers/utils/cx";
const severities = {
  error: "bg-danger-secondary",
  info: "bg-promo-secondary",
  promotion: "bg-layer-floor-1 border-separator",
  success: "bg-positive-secondary",
  warning: "bg-warning-secondary"
};
const actionColors = {
  error: "text-danger-primary",
  info: "text-promo-primary",
  promotion: "text-action-primary",
  success: "text-positive-primary",
  warning: "text-primary"
};
export const DEFAULT_CLOSE_BUTTON_ARIA_LABEL = "close notification";
export const Alert = /*#__PURE__*/React.forwardRef(function AlertComponent(_ref, ref) {
  let {
    children,
    action,
    closeButtonAriaLabel = DEFAULT_CLOSE_BUTTON_ARIA_LABEL,
    icon,
    onClose,
    overrideClassName,
    severity = "success",
    title
  } = _ref;
  const isError = severity === "error";
  const generatedId = useId();
  const contentId = `kalep-alert-content-${generatedId}`;
  const titleId = `kalep-alert-title-${generatedId}`;
  const actionButton = action ? /*#__PURE__*/React.createElement(GhostButton, {
    overrideClassName: cx("mt-5", "bolt-font-body-m-accent", actionColors[severity]),
    onClick: action.onClick
  }, action.label) : null;
  return /*#__PURE__*/React.createElement("div", {
    className: cx("flex w-full gap-4 p-4", "border border-solid border-transparent rounded-md" /* transparent border by default for unified look */, "items-center", "bolt-font-body-s-regular", severities[severity], overrideClassName),
    role: "region",
    "aria-labelledby": cx(title && titleId, contentId),
    ref: ref
  }, /*#__PURE__*/React.createElement(AlertIconWrap, {
    icon: icon,
    severity: severity
  }), /*#__PURE__*/React.createElement("div", {
    className: cx("flex flex-col items-start gap-2 w-full"),
    role: isError ? "alert" : "status",
    "aria-live": isError ? "assertive" : "polite"
  }, title && /*#__PURE__*/React.createElement("div", {
    id: titleId,
    className: cx("text-primary font-semibold")
  }, title), /*#__PURE__*/React.createElement("div", {
    id: contentId,
    className: cx(title ? "text-secondary" : "text-primary")
  }, children), actionButton), onClose && /*#__PURE__*/React.createElement("button", {
    className: cx( /* resets */"border-none bg-transparent cursor-pointer", "mb-auto p-1"),
    type: "button",
    "aria-label": closeButtonAriaLabel,
    onClick: onClose
  }, /*#__PURE__*/React.createElement(Cross, {
    size: "xs",
    className: cx("block")
  })));
});
const iconColors = {
  error: "text-danger-secondary",
  info: "text-promo-secondary",
  promotion: "text-primary",
  success: "text-action-secondary",
  warning: "text-warning-secondary"
};
function AlertIconWrap(_ref2) {
  let {
    icon: iconProp,
    severity
  } = _ref2;
  if (iconProp === false) {
    return null;
  }
  const commonProps = {
    "aria-hidden": "true",
    className: "block",
    size: "lg"
  };
  let icon = iconProp;
  if (!icon) {
    switch (severity) {
      case "error":
        icon = /*#__PURE__*/React.createElement(AlertIcon, _extends({
          "data-testid": "alert-error-icon"
        }, commonProps));
        break;
      case "info":
        icon = /*#__PURE__*/React.createElement(InfoCircle, _extends({
          "data-testid": "alert-info-icon"
        }, commonProps));
        break;
      case "success":
        icon = /*#__PURE__*/React.createElement(CheckCircleFill, _extends({
          "data-testid": "alert-success-icon"
        }, commonProps));
        break;
      case "promotion":
        icon = /*#__PURE__*/React.createElement(Offer, _extends({
          "data-testid": "alert-promotion-icon"
        }, commonProps));
        break;
      case "warning":
        icon = /*#__PURE__*/React.createElement(AlertIcon, _extends({
          "data-testid": "alert-warning-icon"
        }, commonProps));
        break;
      default:
        return null;
    }
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cx("self-start", iconColors[severity])
  }, icon);
}